import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * Reusable help button component for charts
 * Shows a ? icon that opens a popup balloon with chart explanation
 *
 * @param {Object} props
 * @param {string} props.title - Chart title for the popup header
 * @param {string} props.description - Brief description of what the chart shows
 * @param {string} props.className - Additional CSS classes
 */
const ChartHelpButton = ({ title, description, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10 ${className}`}
        aria-label="Chart help"
      >
        <HelpCircle size={16} />
      </button>

      {isOpen && (
        <div
          ref={popupRef}
          className="absolute left-0 top-full mt-2 z-50 w-72 bg-[#252525] border border-[#3a3c3b] rounded-lg shadow-xl"
        >
          {/* Arrow */}
          <div className="absolute -top-2 left-3 w-3 h-3 bg-[#252525] border-l border-t border-[#3a3c3b] transform rotate-45" />

          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#3a3c3b]">
            <span className="text-white text-sm font-medium">{title}</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          {/* Content */}
          <div className="px-3 py-2">
            <p className="text-gray-300 text-xs leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartHelpButton;
